import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CodexRail } from "@/components/site/CodexRail";
import { useEffect } from "react";
import { getWisdomPost } from "@/lib/wisdom.functions";
import type { PublicWisdomPost } from "@/lib/wisdom-kinds";
import { getReleaseBySlug } from "@/lib/music.functions";
import { getBookBySlug } from "@/lib/books.functions";
import { getWisdomTags } from "@/lib/wisdom-tags.functions";
import { getLessonsByTopics, type LessonTopicMatch } from "@/lib/topic-links.functions";
import { booksForTopics, topicLabel } from "@/lib/topics";
import { activationTitle } from "@/lib/cross-links";

import { ArticleBody } from "@/components/site/ArticleBody";
import { ArticleConversion } from "@/components/site/ArticleConversion";
import { RelatedRail } from "@/components/site/RelatedRail";
import { FavoriteButton } from "@/components/site/FavoriteButton";
import { ReadingChrome } from "@/components/site/ReadingChrome";
import { RecentlyViewedRail } from "@/components/site/RecentlyViewedRail";
import { trackViewed } from "@/hooks/use-recently-viewed";
import { ArrowLeft, Share2 } from "lucide-react";
import { absoluteUrl } from "@/lib/site";
import {
  PORTRAIT_ALT,
  PORTRAIT_URL,
  breadcrumbs,
  ldScript,
  orgRef,
  personRef,
  seoMeta,
  websiteRef,
} from "@/lib/schema";



type Companion = {
  section: "music" | "books" | "activations";
  slug: string;
  to: string;
  title: string;
  kind: string;
};

export const Route = createFileRoute("/wisdom/$slug")({
  loader: async ({ params }) => {
    // The database is the source of truth; drafts and hidden rows 404.
    const found = await getWisdomPost({ data: { slug: params.slug } }).catch(() => null);
    if (!found) throw notFound();
    const post = found.post;
    // Companion works are declared on the post itself; resolve real titles so
    // every internal link uses descriptive, first-party anchor text.
    const musicSlug = post.relatedReleaseSlug ?? post.relatedMusic ?? null;
    const bookSlug = post.relatedBook ?? null;
    const [release, book, editorialTags] = await Promise.all([
      musicSlug ? getReleaseBySlug({ data: { slug: musicSlug } }).catch(() => null) : null,
      bookSlug ? getBookBySlug({ data: { slug: bookSlug } }).catch(() => null) : null,
      getWisdomTags({ data: { slug: params.slug } }).catch(() => [] as string[]),
    ]);
    // Author-written topics (admin editor) are the only source of topic edges.
    const tags = post.tags?.length ? post.tags : editorialTags;
    const lessons = tags.length
      ? await getLessonsByTopics({ data: { tags, limit: 3 } }).catch(() => [])
      : [];
    const topicBooks = booksForTopics(tags, { excludeSlug: bookSlug ?? undefined, limit: 2 });

    const companions: Companion[] = [];
    if (release) companions.push({ section: "music", slug: release.slug, to: `/music/${release.slug}`, title: release.title, kind: `${release.type} by Buddha KA` });
    if (book) companions.push({ section: "books", slug: book.slug, to: `/books/${book.slug}`, title: book.title, kind: "Book by Buddha KA" });
    if (post.relatedActivation) {
      const t = activationTitle(post.relatedActivation);
      if (t) companions.push({ section: "activations", slug: post.relatedActivation, to: `/activations/${post.relatedActivation}`, title: t, kind: "Daily activation" });
    }
    return {
      post,
      companions,
      alsoRead: found.related,
      tags,
      lessons,
      topicBooks: topicBooks.map((m) => ({ slug: m.book.slug, title: m.book.title, topics: m.topics })),
    };

  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Not found · Buddha KA" }, { name: "robots", content: "noindex" }] };
    const p = loaderData.post;
    const path = p.canonicalPath ?? `/wisdom/${p.slug}`;
    const url = absoluteUrl(path);
    const description = p.metaDescription ?? p.excerpt;
    const image = p.ogImageUrl ? absoluteUrl(p.ogImageUrl) : PORTRAIT_URL;
    const companions = loaderData.companions ?? [];
    return {
      ...seoMeta({
        title: p.metaTitle ?? `${p.title} · Buddha KA`,
        description,
        path,
        image,
        imageAlt: p.ogImageUrl ? p.title : PORTRAIT_ALT,
        ogType: "article",
      }),
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${url}#article`,
          headline: p.title,
          description,
          url,
          image,
          mainEntityOfPage: { "@type": "WebPage", "@id": url, url },
          isPartOf: websiteRef(),
          author: personRef(),
          creator: personRef(),
          publisher: orgRef(),
          inLanguage: "en",
          articleSection: p.category,
          ...(p.publishedAt ? { datePublished: p.publishedAt } : {}),
          ...(p.updatedAt ? { dateModified: p.updatedAt } : {}),
          ...(loaderData.tags?.length ? { keywords: loaderData.tags.join(", ") } : {}),
          about: [personRef(), orgRef()],

          ...(companions.length
            ? {
                mentions: companions.map((c) => ({
                  "@type": "CreativeWork",
                  name: c.title,
                  url: absoluteUrl(c.to),
                })),
                relatedLink: companions.map((c) => absoluteUrl(c.to)),
              }
            : {}),
        }),
        ldScript(breadcrumbs([["Wisdom", "/wisdom"], [p.title, path]])),
      ],
    };
  },


  component: WisdomPost,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">Reading not found.</p>
      <Link to="/wisdom" className="mt-4 inline-block text-gold-luminous">← Return to the reading room</Link>
    </div>
  ),
});

function WisdomPost() {
  const { post: p, companions, alsoRead, tags, lessons, topicBooks } = Route.useLoaderData() as {
    post: PublicWisdomPost;
    companions: Companion[];
    alsoRead: PublicWisdomPost[];
    tags: string[];
    lessons: LessonTopicMatch[];
    topicBooks: { slug: string; title: string; topics: string[] }[];
  };




  useEffect(() => {
    trackViewed({ kind: "wisdom", slug: p.slug, title: p.title, href: `/wisdom/${p.slug}` });
  }, [p.slug, p.title]);

  const share = () => {
    if (typeof window === "undefined") return;
    const nav = window.navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      void nav.share({ title: p.title, text: p.excerpt, url: window.location.href }).catch(() => {});
    } else if (nav.clipboard) {
      void nav.clipboard.writeText(window.location.href);
    }
  };

  return (
    <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-20">

      <ReadingChrome completionKey={`wisdom:${p.slug}`} />
      <Link to="/wisdom" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold hover-glow">
        <ArrowLeft size={14} /> Wisdom
      </Link>

      <header className="mt-8">
        <p className="text-xs uppercase tracking-[0.25em] text-gold">{p.category} · {p.readingTime} · by Buddha KA</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight sm:text-5xl md:text-6xl">{p.title}</h1>
        <p className="mt-6 font-serif text-xl italic text-ivory/90">{p.excerpt}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          <FavoriteButton kind="wisdom" slug={p.slug} />
          <button
            onClick={share}
            className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold"
            aria-label="Share this reading"
          >
            <Share2 size={12} /> Share
          </button>
        </div>
      </header>

      <ArticleBody body={p.body} />

      <blockquote className="my-12 border-l-2 border-gold pl-6 py-2">
        <p className="font-serif text-2xl italic text-gold-luminous sm:text-3xl">&ldquo;{p.pullQuote}&rdquo;</p>
      </blockquote>

      {companions.length > 0 && (
        <nav aria-label="Companion work" className="mt-4 rounded-2xl border hairline bg-charcoal p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Companion work to this writing</p>
          <ul className="mt-4 space-y-3 text-sm">
            {companions.map((c) => (
              <li key={c.to}>
                {c.section === "music" ? (
                  <Link to="/music/$slug" params={{ slug: c.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">{c.title}</Link>
                ) : c.section === "books" ? (
                  <Link to="/books/$slug" params={{ slug: c.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">{c.title}</Link>
                ) : (
                  <Link to="/activations/$slug" params={{ slug: c.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">{c.title}</Link>
                )}
                <span className="text-muted-foreground"> — {c.kind}</span>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {alsoRead.length > 0 && (
        <nav aria-label="Related writings" className="mt-6 border-t hairline pt-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Writings that share this thread</p>
          <ul className="mt-4 space-y-2 text-sm">
            {alsoRead.map((w) => (
              <li key={w.slug}>
                <Link to="/wisdom/$slug" params={{ slug: w.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
                  {w.title}
                </Link>
                <span className="text-muted-foreground"> — {w.category}</span>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {(lessons.length > 0 || topicBooks.length > 0) && (
        <nav aria-label="Study this subject" className="mt-6 border-t hairline pt-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Study {topicLabel(tags) || "this subject"} further
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            {lessons.map((l) => (
              <li key={`lesson-${l.slug}`}>
                <Link
                  to="/academy/$domain/lesson/$lesson"
                  params={{ domain: l.domainSlug, lesson: l.slug }}
                  className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
                >
                  {l.title}
                </Link>
                <span className="text-muted-foreground"> — Academy lesson in {l.domainName}</span>
              </li>
            ))}
            {topicBooks.map((b) => (
              <li key={`book-${b.slug}`}>
                <Link to="/books/$slug" params={{ slug: b.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
                  {b.title}
                </Link>
                <span className="text-muted-foreground"> — Book by Buddha KA</span>
              </li>
            ))}
          </ul>
        </nav>
      )}


      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <CodexRail entityKind="wisdom" entityRef={p.slug} heading="Concepts this writing explores" />
      </div>
      <RelatedRail kind="wisdom" slug={p.slug} heading="Keep going" />
      <RecentlyViewedRail excludeSlug={p.slug} />


      <ArticleConversion
        articleSlug={p.slug}
        primary={
          companions.find((c) => c.section === "music")
            ? {
                to: companions.find((c) => c.section === "music")!.to,
                label: `Listen to ${companions.find((c) => c.section === "music")!.title}`,
                detail: "The companion release to this writing.",
              }
            : { to: "/music", label: "Listen to the Buddha KA catalog" }
        }
        secondary={[
          ...companions
            .filter((c) => c.section !== "music")
            .map((c) => ({ to: c.to, label: c.title, detail: c.kind })),
          { to: "/activations", label: "Begin today's activation", detail: "A daily practice" },
          { to: "/shop", label: "Visit the shop", detail: "Books, music, and tools" },
        ]}
        newsletterSource={`wisdom:${p.slug}`}
      />

    </article>
  );
}
